import Container from '@mui/material/Container'
import { CreateCampaignHeader } from '~/features/campaign_create/Header'
import { CreateCampaignContext } from '~/contexts/CreateCampaignContext'
import { FC, useContext } from 'react'
import { NavigationButtons } from './NavigationButtons'
import { useCampaignPageStepForm } from './useCampaignPageStepForm'
import { FormProvider } from 'react-hook-form'
import { z } from 'zod'
import { CampaignCategory, CampaignCostHandlingMethod, SearchGenre } from '~/generated/graphql'
import { CampaignInputComponent } from '../campaign_input/CampaignInputComponent'
import { Box, Stack, Typography } from '@mui/material'
import { LinkToInfo } from '~/components/common/LinkToInfo'
import { CampaignPublishSchedulePicker } from '~/features/campaigns/regularlyPublish/CampaignPublishSchedulePicker'
import { reservationScheduleSchemaFields } from '../campaign_input/type'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'

const ReservationScheduleSchema = z.object({
  category: z.literal(CampaignCategory.Product).or(z.literal(CampaignCategory.Service)),
  searchGenre: z.nativeEnum(SearchGenre),
  costHandlingMethod: z.nativeEnum(CampaignCostHandlingMethod),
  ...reservationScheduleSchemaFields,
})

export function validateReservationSchedule<T extends z.infer<typeof ReservationScheduleSchema>>(
  v: T,
  ctx: z.RefinementCtx,
) {
  if (
    v.costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign ||
    v.category === CampaignCategory.Product ||
    v.searchGenre === SearchGenre.Travel
  ) {
    return {
      ...v,
      schedules: null,
    }
  }
  if (!v.schedules || !Object.values(v.schedules).some(Boolean)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '来店可能な曜日を選択してください',
      path: ['schedules'],
    })
    return z.NEVER
  }
  return v
}

export const ReservationScheduleContents: FC = () => {
  const { campaign } = useContext(CreateCampaignContext)

  const { onSubmit, onBack, useFormMethods } = useCampaignPageStepForm({
    defaultValues: {
      category: campaign.category,
      searchGenre: campaign.searchGenre,
      costHandlingMethod: campaign.costHandlingMethod,
      schedules: campaign.schedules,
    },
    schema: ReservationScheduleSchema.superRefine(validateReservationSchedule),
  })
  return (
    <FormProvider {...useFormMethods}>
      <form onSubmit={onSubmit}>
        <CreateCampaignHeader title="来店日時に関する情報について教えてください" onBack={onBack} />
        <Container sx={{ display: 'flex', flexDirection: 'column' }} maxWidth="sm">
          <CampaignInputComponent
            title="日程調整機能"
            required
            tooltip={
              <Stack gap={2}>
                <Typography variant="body2">
                  インフルエンサーが来店可能な曜日を設定してください。
                  <br />
                  キャンペーン承認後、来店可能日時の設定について日付指定での変更が可能となります。
                </Typography>
                <LinkToInfo
                  href={EXTERNAL_LINKS.SCHEDULE_ADJUSTMENT}
                  linkText="こちら"
                  variant="body2"
                />
              </Stack>
            }
          >
            <Box pt={2}>
              <CampaignPublishSchedulePicker name="schedules" />
            </Box>
          </CampaignInputComponent>
          <NavigationButtons />
        </Container>
      </form>
    </FormProvider>
  )
}
